/* ---------------------------------------------------------------------------
   The wording of recommended remedial work — a reference table, kept by the
   administrator beside the district rates.

   What is edited is the words only: the work item and its note. Which defect
   and size calls for the treatment, its class and its unit are the rules in
   src/data/remedial.ts — the class decides whether the register may price the
   work, and the unit is what an approved rate is checked against.
   --------------------------------------------------------------------------- */

import { h } from '../core/dom.ts';
import { actions, type State } from '../core/store.ts';
import { button, scrim } from '../components/widgets.ts';
import { defectCodeLabel } from '../data/defects.ts';
import {
  CLASS_DIAGNOSTIC,
  isCostable,
  TREATMENT_RULES,
  worded,
  wordingMap,
  type Treatment,
} from '../data/remedial.ts';

/** Every treatment once, with the defect code it is recommended for, in rule order. */
function treatments(): { code: string; treatment: Treatment }[] {
  const seen = new Set<string>();
  const out: { code: string; treatment: Treatment }[] = [];
  for (const rule of TREATMENT_RULES) {
    if (seen.has(rule.treatment.id)) continue;
    seen.add(rule.treatment.id);
    out.push({ code: rule.code, treatment: rule.treatment });
  }
  return out;
}

/** The dialog, or null when it is closed. */
export function treatmentsDialog(s: Readonly<State>): HTMLElement | null {
  if (!s.treatmentsOpen) return null;

  const wording = wordingMap(s.treatmentWording);
  const edit = s.treatmentEdit;
  const live = s.backend === 'live';

  return h(
    'div',
    { class: 'dlg', ...scrim(() => actions.closeTreatments(), () => s.treatmentSaving) },
    h(
      'form',
      {
        class: 'dlg__panel dlg__panel--compact',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': 'Recommended work',
        onsubmit: (e: Event) => {
          e.preventDefault();
          void actions.saveTreatment();
        },
      },
      h(
        'div',
        { class: 'dlg__head' },
        h(
          'div',
          {},
          h('div', { class: 'dlg__kicker', text: 'reference table · one treatment per issue and size' }),
          h('h2', { class: 'dlg__title', text: 'Recommended work' }),
        ),
        h('span', { class: 'spacer' }),
        h(
          'button',
          {
            type: 'button',
            class: 'dlg__close',
            title: 'Close',
            'aria-label': 'Close',
            onclick: () => actions.closeTreatments(),
          },
          '×',
        ),
      ),

      s.treatmentError
        ? h('div', { class: 'dlg__note dlg__note--error', text: s.treatmentError })
        : !live
          ? h('div', { class: 'dlg__note', text: 'The register is not connected, so the wording cannot be changed.' })
          : null,

      h(
        'div',
        { class: 'dlg__body' },
        h(
          'table',
          { class: 'tbl treat__tbl' },
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { style: 'width:140px', text: 'Issue' }),
              h('th', { text: 'Recommended work' }),
              h('th', { style: 'width:80px', text: 'Class' }),
              h('th', { style: 'width:44px', text: 'Unit' }),
              h('th', { style: 'width:150px' }),
            ),
          ),
          h(
            'tbody',
            {},
            ...treatments().map(({ code, treatment }) => {
              const t = worded(treatment, wording);
              const stored = wording.get(t.id);
              const editing = edit?.id === t.id;
              return h(
                'tr',
                { class: [editing && 'is-selected'], 'data-treatment': t.id },
                h(
                  'td',
                  {},
                  h('div', { text: defectCodeLabel(code) }),
                  h('code', { class: 'rates__id', text: t.id }),
                ),
                editing
                  ? h(
                      'td',
                      { class: 'treat__edit' },
                      h('input', {
                        class: 'input',
                        'aria-label': 'Work item',
                        required: true,
                        maxlength: '200',
                        value: edit.label,
                        oninput: (e: Event) => actions.setTreatmentField('label', (e.target as HTMLInputElement).value),
                      }),
                      h('textarea', {
                        class: 'input',
                        'aria-label': 'Note',
                        rows: 3,
                        maxlength: '2000',
                        value: edit.note,
                        oninput: (e: Event) =>
                          actions.setTreatmentField('note', (e.target as HTMLTextAreaElement).value),
                      }),
                    )
                  : h(
                      'td',
                      { class: 'tbl__prose' },
                      h('div', { text: t.label }),
                      h('div', { class: 'rem__note', text: t.note }),
                      stored
                        ? h('div', {
                            class: 'treat__edited',
                            text: `Reworded${stored.editedBy ? ` by ${stored.editedBy}` : ''} · ${stored.editedAt.slice(0, 10)}`,
                          })
                        : null,
                    ),
                h('td', {}, h('span', {
                  class: ['rem__class', !isCostable(t.klass) && 'rem__class--design'],
                  title: CLASS_DIAGNOSTIC[t.klass],
                  text: t.klass,
                })),
                h('td', { class: 'nowrap', text: t.unit }),
                h(
                  'td',
                  { class: 'treat__acts' },
                  editing
                    ? [
                        button(s.treatmentSaving ? 'Saving…' : 'Save', {
                          sm: true,
                          primary: true,
                          type: 'submit',
                          disabled: s.treatmentSaving,
                        }),
                        button('Cancel', {
                          sm: true,
                          disabled: s.treatmentSaving,
                          onClick: () => actions.editTreatment(null),
                        }),
                      ]
                    : [
                        button('Edit', {
                          sm: true,
                          disabled: !live || s.treatmentSaving,
                          onClick: () => actions.editTreatment({ id: t.id, label: t.label, note: t.note }),
                        }),
                        stored
                          ? button('Restore', {
                              sm: true,
                              title: 'Put back the built-in wording',
                              disabled: !live || s.treatmentSaving,
                              onClick: () => void actions.restoreTreatment(t.id),
                            })
                          : null,
                      ],
                ),
              );
            }),
          ),
        ),
      ),

      h(
        'div',
        { class: 'dlg__foot' },
        h('span', {
          class: 'dlg__foot-note',
          text: s.treatmentWording.length ? `${s.treatmentWording.length} reworded` : '',
        }),
        h('span', { class: 'spacer' }),
        button('Close', { onClick: () => actions.closeTreatments(), disabled: s.treatmentSaving }),
      ),
    ),
  );
}
